import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AuthRequest, LoginResponse } from '@routes/auth/___shared___/auth.api.ts';
import { login } from '@routes/auth/___shared___/auth.api.ts';
import { refreshToken } from '@routes/auth/___shared___/auth.slice.ts';
import type { LoginErrorResponse } from '@routes/auth/login/___shared___/types.ts';
import type { UpdateUserConsentRequest } from '@routes/auth/login/api/login.api.ts';
import { updateUserConsent } from '@routes/auth/login/api/login.api.ts';
import {
    persistRefreshToken,
    persistRefreshTokenExpiresAtUTC,
    persistToken,
    readToken,
} from '@store/services/localStorage.service.ts';
import { extractError } from '@utils/slice.ts';

type AuthSliceError = Record<string, any> | null;

export interface AuthSlice {
    token: string | null;
    isLoading: boolean;
    error: AuthSliceError;
    email: string;
    userConsentInfo: boolean | null;
    isUpdateUserConsentInfoPending: boolean;
}

const initialState: AuthSlice = {
    isLoading: false,
    error: null,
    token: readToken(),
    email: '',
    userConsentInfo: null,
    isUpdateUserConsentInfoPending: false,
};

export const doLogin = createAsyncThunk<LoginResponse, AuthRequest, { rejectValue: LoginErrorResponse }>(
    'auth/doLogin',
    (loginPayload, { rejectWithValue }) =>
        login(loginPayload)
            .then((res) => {
                persistToken(res.accessToken);
                persistRefreshToken(res.refreshToken);
                persistRefreshTokenExpiresAtUTC(res.refreshTokenExpiresAtUTC);
                return res;
            })
            .catch((err) => {
                return rejectWithValue(err);
            }),
);

export const updateUserConsentInfo = createAsyncThunk<any, UpdateUserConsentRequest, { rejectValue: any }>(
    'auth/updateUserConsentInfo',
    (updateUserConsentPayload, { rejectWithValue }) =>
        updateUserConsent(updateUserConsentPayload)
            .then((res) => {
                return res;
            })
            .catch((err) => {
                return rejectWithValue(err);
            }),
);

const authLoginSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setEmail: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
        },
        setError: (state, action: PayloadAction<AuthSliceError>) => {
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(doLogin.pending, (state) => {
            state.error = null;
            state.isLoading = true;
        });
        builder.addCase(doLogin.fulfilled, (state, action) => {
            state.isLoading = false;
            state.token = action.payload.accessToken;
        });
        builder.addCase(doLogin.rejected, (state, action) => {
            state.isLoading = false;
            state.error = extractError(action);
        });
        builder.addCase(refreshToken.fulfilled, (state, action) => {
            state.token = action.payload.accessToken;
        });
        builder.addCase(updateUserConsentInfo.pending, (state) => {
            state.error = null;
            state.isUpdateUserConsentInfoPending = true;
        });
        builder.addCase(updateUserConsentInfo.fulfilled, (state) => {
            state.userConsentInfo = true;
            state.isUpdateUserConsentInfoPending = false;
        });
        builder.addCase(updateUserConsentInfo.rejected, (state, action) => {
            state.error = extractError(action);
            state.isUpdateUserConsentInfoPending = false;
        });
    },
});

export const { setEmail, setError } = authLoginSlice.actions;

export default authLoginSlice.reducer;
